import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Heart, Droplets, Weight, Activity, X, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VITAL_TYPES = [
  { key: 'blood_pressure', label: 'Tansiyon', icon: Activity, color: '#EF4444', glow: 'rgba(239,68,68,0.15)', route: '/api/vitals/blood-pressure', unit: 'mmHg' },
  { key: 'heart_rate', label: 'Nabiz', icon: Heart, color: '#F59E0B', glow: 'rgba(245,158,11,0.15)', route: '/api/vitals/heart-rate', unit: 'bpm' },
  { key: 'glucose', label: 'Kan Sekeri', icon: Droplets, color: '#3B82F6', glow: 'rgba(59,130,246,0.15)', route: '/api/vitals/glucose', unit: 'mg/dL' },
  { key: 'weight', label: 'Kilo', icon: Weight, color: '#22C55E', glow: 'rgba(34,197,94,0.15)', route: '/api/vitals/weight', unit: 'kg' },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px', padding: '10px 12px', fontSize: '14px', color: '#F1F5F9',
  outline: 'none', width: '100%',
};

const STATUS_COLORS = { green: '#22C55E', yellow: '#EAB308', red: '#EF4444' };

function formatValue(type, data) {
  if (!data) return null;
  if (type === 'blood_pressure') return `${data.systolic}/${data.diastolic}`;
  if (type === 'heart_rate') return `${data.bpm}`;
  if (type === 'glucose') return `${data.value}`;
  if (type === 'weight') return `${data.kg}`;
  return null;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Dun';
  return `${diff} gun once`;
}

function TrendIcon({ trend, size = 12 }) {
  if (trend === 'artiyor') return <TrendingUp style={{ width: size, height: size, color: '#EF4444' }} />;
  if (trend === 'azaliyor') return <TrendingDown style={{ width: size, height: size, color: '#22C55E' }} />;
  return <Minus style={{ width: size, height: size, color: '#64748B' }} />;
}

const VitalInputModal = ({ vital, onClose, onSaved }) => {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = {};
      if (vital.key === 'blood_pressure') {
        payload = { systolic: parseInt(values.systolic), diastolic: parseInt(values.diastolic) };
      } else if (vital.key === 'heart_rate') {
        payload = { bpm: parseInt(values.bpm), rhythm: values.rhythm || 'normal' };
      } else if (vital.key === 'glucose') {
        payload = { value: parseFloat(values.value), timing: values.timing || 'fasting' };
      } else if (vital.key === 'weight') {
        payload = { kg: parseFloat(values.kg) };
      }
      await axios.post(`${API}${vital.route}`, payload, { withCredentials: true });
      onSaved();
      onClose();
    } catch {
      alert('Kaydedilemedi, lütfen tekrar deneyin.');
    } finally { setSaving(false); }
  };

  const canSave = () => {
    if (vital.key === 'blood_pressure') return values.systolic && values.diastolic;
    if (vital.key === 'heart_rate') return values.bpm;
    if (vital.key === 'glucose') return values.value;
    if (vital.key === 'weight') return values.kg;
    return false;
  };

  const Icon = vital.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }} data-testid={`vital-modal-${vital.key}`}>
      <div className="max-w-sm w-full" style={{
        background: 'rgba(16,24,48,0.95)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', padding: '24px', boxShadow: `0 20px 50px rgba(0,0,0,0.4), 0 0 20px ${vital.glow}`,
      }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: vital.glow }}>
              <Icon className="w-5 h-5" style={{ color: vital.color }} />
            </div>
            <h3 style={{ color: '#F1F5F9', fontSize: '17px', fontWeight: 700 }}>{vital.label} Ekle</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 transition-colors" data-testid="vital-modal-close">
            <X className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
          </button>
        </div>

        <div className="space-y-3 mb-5">
          {vital.key === 'blood_pressure' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#94A3B8' }}>Sistolik</label>
                <input type="number" value={values.systolic || ''} onChange={e => setValues(p => ({ ...p, systolic: e.target.value }))}
                  placeholder="120" style={inputStyle} data-testid="vital-systolic" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#94A3B8' }}>Diastolik</label>
                <input type="number" value={values.diastolic || ''} onChange={e => setValues(p => ({ ...p, diastolic: e.target.value }))}
                  placeholder="80" style={inputStyle} data-testid="vital-diastolic" />
              </div>
            </div>
          )}
          {vital.key === 'heart_rate' && (
            <>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#94A3B8' }}>Nabiz (bpm)</label>
                <input type="number" value={values.bpm || ''} onChange={e => setValues(p => ({ ...p, bpm: e.target.value }))}
                  placeholder="72" style={inputStyle} data-testid="vital-bpm" />
              </div>
              <div className="flex gap-2">
                {['normal', 'irregular'].map(r => (
                  <button key={r} type="button" onClick={() => setValues(p => ({ ...p, rhythm: r }))}
                    className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: (values.rhythm || 'normal') === r ? vital.glow : 'rgba(255,255,255,0.04)',
                      color: (values.rhythm || 'normal') === r ? vital.color : '#94A3B8',
                      border: (values.rhythm || 'normal') === r ? `1px solid ${vital.color}40` : '1px solid rgba(255,255,255,0.06)',
                    }} data-testid={`vital-rhythm-${r}`}>
                    {r === 'normal' ? 'Normal' : 'Düzensiz'}
                  </button>
                ))}
              </div>
            </>
          )}
          {vital.key === 'glucose' && (
            <>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: '#94A3B8' }}>Kan Sekeri (mg/dL)</label>
                <input type="number" value={values.value || ''} onChange={e => setValues(p => ({ ...p, value: e.target.value }))}
                  placeholder="100" style={inputStyle} data-testid="vital-glucose" />
              </div>
              <div className="flex gap-2">
                {[{ v: 'fasting', l: 'Aclik' }, { v: 'postprandial', l: 'Tokluk' }, { v: 'random', l: 'Rastgele' }].map(t => (
                  <button key={t.v} type="button" onClick={() => setValues(p => ({ ...p, timing: t.v }))}
                    className="flex-1 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: (values.timing || 'fasting') === t.v ? vital.glow : 'rgba(255,255,255,0.04)',
                      color: (values.timing || 'fasting') === t.v ? vital.color : '#94A3B8',
                      border: (values.timing || 'fasting') === t.v ? `1px solid ${vital.color}40` : '1px solid rgba(255,255,255,0.06)',
                    }} data-testid={`vital-timing-${t.v}`}>
                    {t.l}
                  </button>
                ))}
              </div>
            </>
          )}
          {vital.key === 'weight' && (
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: '#94A3B8' }}>Kilo (kg)</label>
              <input type="number" step="0.1" value={values.kg || ''} onChange={e => setValues(p => ({ ...p, kg: e.target.value }))}
                placeholder="75.0" style={inputStyle} data-testid="vital-kg" />
            </div>
          )}
        </div>

        <button onClick={handleSave} disabled={!canSave() || saving}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: `linear-gradient(135deg, ${vital.color}, ${vital.color}CC)`,
            color: 'white', border: 'none', cursor: 'pointer',
            boxShadow: `0 6px 20px ${vital.glow}`,
          }} data-testid="vital-save-btn">
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </div>
  );
};

const DailyVitalsCard = ({ cardioScore }) => {
  const [analysis, setAnalysis] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const isHighlighted = (cardioScore || 0) > 30;

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vitals/latest`, { withCredentials: true });
      setAnalysis(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAnalysis(); }, [fetchAnalysis]);

  const latest = analysis?.latest || {};
  const classifications = analysis?.classifications || {};
  const trends = analysis?.trends || {};
  const riskFlags = analysis?.risk_flags || [];
  const hasWarnings = riskFlags.length > 0;

  return (
    <>
      <div className="relative overflow-hidden" style={{
        background: 'rgba(16,24,48,0.75)',
        border: hasWarnings ? '1px solid rgba(239,68,68,0.15)' : isHighlighted ? '1px solid rgba(239,68,68,0.1)' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px', padding: '20px',
        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        boxShadow: hasWarnings ? '0 0 25px rgba(239,68,68,0.12)' : '0 0 20px rgba(255,200,100,0.08)',
      }} data-testid="daily-vitals-card">

        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
            background: hasWarnings ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
          }}>
            <Activity className="w-4 h-4" style={{ color: hasWarnings ? '#EF4444' : '#F59E0B' }} />
          </div>
          <div>
            <h3 style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>Günlük Sağlık Verileri</h3>
            <p style={{ color: '#64748B', fontSize: '11px', marginTop: '2px' }}>Bugunku olcumlerini girerek daha dogru analiz al</p>
          </div>
        </div>

        {/* Risk warning banner */}
        {hasWarnings && (
          <div className="mb-3 px-3 py-2 rounded-lg flex items-start gap-2" style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.12)',
          }} data-testid="vitals-warning-banner">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
            <div>
              {riskFlags.slice(0, 2).map((rf, i) => (
                <p key={i} style={{ fontSize: '11px', color: '#FCA5A5', lineHeight: 1.5 }}>{rf.message}</p>
              ))}
              <p style={{ fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>Bu değerler normalin üzerinde görünüyor. Takip etmek önemli.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          {VITAL_TYPES.map(v => {
            const Icon = v.icon;
            const data = latest[v.key];
            const val = formatValue(v.key, data);
            const dateStr = data ? formatDate(data.created_at) : null;
            const cls = classifications[v.key] || {};
            const statusColor = STATUS_COLORS[cls.color] || v.color;
            const trend = trends[v.key] || {};
            const trendDir = trend.trend_3d || 'insufficient';

            return (
              <button key={v.key} onClick={() => setActiveModal(v)}
                className="text-left p-3 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:scale-[1.02] group"
                style={{
                  background: val ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
                  border: val ? `1px solid ${statusColor}25` : '1px solid rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                }}
                data-testid={`vital-btn-${v.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all group-hover:scale-110" style={{
                    background: val ? `${statusColor}18` : v.glow,
                    boxShadow: `0 0 10px ${val ? statusColor + '15' : v.glow}`,
                  }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: val ? statusColor : v.color }} />
                  </div>
                  <span style={{ color: '#94A3B8', fontSize: '12px', fontWeight: 600 }}>{v.label}</span>
                  {val && trendDir !== 'insufficient' && (
                    <TrendIcon trend={trendDir} />
                  )}
                </div>
                {val ? (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span style={{ color: statusColor, fontSize: '18px', fontWeight: 700 }}>{val}</span>
                      <span style={{ color: '#64748B', fontSize: '10px' }}>{v.unit}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {cls.label && cls.color !== 'green' && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{
                          background: `${statusColor}15`, color: statusColor,
                        }} data-testid={`vital-status-${v.key}`}>{cls.label}</span>
                      )}
                      <span style={{ color: '#475569', fontSize: '10px' }}>{dateStr}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span style={{ color: v.color, fontSize: '12px', fontWeight: 600, opacity: 0.7 }}>+ Ekle</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {activeModal && (
        <VitalInputModal vital={activeModal} onClose={() => setActiveModal(null)} onSaved={fetchAnalysis} />
      )}
    </>
  );
};

export default DailyVitalsCard;
